import { useTranslation } from 'react-i18next'

import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card'

/** Assumed for an HTML asset the server could not read a size from. */
export const DEFAULT_HTML_SIZE = { width: 300, height: 250 }

interface ScaledHtmlFrameProps {
  /** The markup to render, or… */
  html?: string
  /** …the address of an HTML file, such as an uploaded asset on the CDN. */
  src?: string
  width: number
  height: number
  /** Largest box the frame may take; the creative is scaled down to fit it. */
  maxWidth: number
  maxHeight: number
  /** False for thumbnails, so the hover reaches the trigger instead of the ad. */
  interactive?: boolean
}

/** Renders HTML in a sandboxed iframe at its own size, scaled down into a box. */
export function ScaledHtmlFrame({
  html,
  src,
  width,
  height,
  maxWidth,
  maxHeight,
  interactive = true,
}: ScaledHtmlFrameProps) {
  const { t } = useTranslation()
  const scale = Math.min(1, maxWidth / width, maxHeight / height)
  return (
    <div className="overflow-hidden" style={{ width: width * scale, height: height * scale }}>
      <iframe
        title={t('creatives.preview')}
        srcDoc={html}
        src={html === undefined ? src : undefined}
        width={width}
        height={height}
        scrolling="no"
        loading="lazy"
        sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
        className={interactive ? 'block border-0' : 'pointer-events-none block border-0'}
        style={{ transform: scale === 1 ? undefined : `scale(${scale})`, transformOrigin: '0 0' }}
      />
    </div>
  )
}

interface HtmlPreviewThumbnailProps {
  html: string
  width: number
  height: number
}

/**
 * A small live rendering of an HTML creative that shows it at full size on
 * hover — for judging creatives in a list before they exist on the server.
 */
export function HtmlPreviewThumbnail({ html, width, height }: HtmlPreviewThumbnailProps) {
  if (!(width > 0 && height > 0)) return null
  return (
    <HoverCard openDelay={250} closeDelay={80}>
      <HoverCardTrigger asChild>
        <span className="inline-flex size-16 items-center justify-center rounded-sm border border-(--ad-border) bg-(--ad-neutral-soft)">
          <ScaledHtmlFrame
            html={html}
            width={width}
            height={height}
            maxWidth={60}
            maxHeight={60}
            interactive={false}
          />
        </span>
      </HoverCardTrigger>
      <HoverCardContent side="right" className="w-auto p-2">
        <ScaledHtmlFrame html={html} width={width} height={height} maxWidth={640} maxHeight={640} />
      </HoverCardContent>
    </HoverCard>
  )
}
